// The browser-side tone for a notification that just arrived in this tab
// (issue #5890). Three short tones synthesised with the Web Audio API — no
// asset file, no `<audio>`. Each tone is an `OscillatorNode` envelope over a
// `GainNode`: a fast attack, a brief hold, and an exponential release to a
// small positive floor (`exponentialRampToValueAtTime` refuses 0, so the
// floor is `0.0001`). The peak gain is at most 0.15 and the tone ends inside
// 400 ms of its start. No shape lives in here without a guard the tests
// pin against LITERAL numbers (0.15, 400), never against a constant read from here.
//
// The synthesis is behind an injected `SoundRuntime` seam so the logic is
// unit-testable in happy-dom, which has no `AudioContext`: the tests pass a
// fake that records every `(frequency, type, start, stop, peak)` it was
// asked for, and the real `browserSoundRuntime()` wires the seam to the live
// APIs. `playNotificationSound(choice, runtime)` is the public entry: it
// NEVER throws — a missing or `suspended` context, an Off choice, an
// `OscillatorNode` that fails to `start`, are all answered with `false`,
// not a thrown promise. The bell and the page's test button both call the
// module-level `playStoredNotificationSound()`, which reads the stored choice
// (`local-pref.ts`) and plays it.
//
// `AudioContext` is created once, on first call, and reused; a fresh context
// has the `suspended` state until the user has interacted with the page
// (the browser's autoplay policy), and `resume()` is the only request that
// lifts it. A context that stays `suspended` after `resume()` returns
// `false` — a freshly loaded tab can be silent for a real arrival until the
// first click, which the page's hint line says.

import { withDeadline } from '@developerz/kit';
import { z } from 'zod';
import { readLocalPref, writeLocalPref } from './local-pref';

// The three sounds the page exposes. `as const` so a `Tone` is the LITERAL
// `'chime' | 'ping' | 'pop'` and the test fence can read its members.
export const NOTIFICATION_SOUNDS = ['chime', 'ping', 'pop'] as const;
export type NotificationSound = (typeof NOTIFICATION_SOUNDS)[number];

/** The choices the `<select>` exposes: `off` plus every tone. */
export const SOUND_CHOICES = ['off', ...NOTIFICATION_SOUNDS] as const;
export type SoundChoice = (typeof SOUND_CHOICES)[number];

/** The key the stored choice lives under. */
export const NOTIFICATION_SOUND_KEY = 'dz.notification-sound';

/** A tone's peak gain in linear amplitude. The tests pin against this LITERAL. */
export const NOTIFICATION_SOUND_PEAK_GAIN = 0.15;

/**
 * How long a play waits for a suspended context to resume. A context created
 * outside a user gesture stays suspended and its `resume()` stays pending until
 * the next gesture: waiting longer would play a stale arrival's tone at the
 * person's next click, so after this bound the play answers `false` and the late
 * `resume()` settles on its own and plays nothing.
 */
const RESUME_WAIT_MS = 300;

const SOUND_CHOICE_SCHEMA = z.enum(SOUND_CHOICES);

/**
 * One sound's bus (#6773's transfer sounds): every tone of the sound runs through ONE gain of
 * `gain`, a compressor, and an echo of `echoMs` fed back at `feedback` and mixed in at `wet`.
 * Its nodes are released `releaseMs` after it is made.
 */
export type SoundBus = {
  readonly gain: number;
  readonly echoMs: number;
  readonly feedback: number;
  readonly wet: number;
  readonly releaseMs: number;
};

/** One tone `SoundRuntime.playTone` plays. */
export type ToneSpec = {
  readonly frequency: number;
  readonly toFrequency?: number;
  readonly type: 'sine' | 'triangle' | 'square' | 'sawtooth';
  readonly peak: number;
  readonly startMs: number;
  readonly stopMs: number;
  /** A low-pass filter (Q 6) at this frequency; none when absent (#6773). */
  readonly lowpassHz?: number;
  /** The linear attack to `peak`; 5 ms when absent, every notification tone and Fleet cue. */
  readonly attackMs?: number;
  /** The bus `SoundRuntime.bus` made; the context's destination when absent. */
  readonly bus?: unknown;
};

/**
 * The browser-facing seam. `browserSoundRuntime()` is the real impl; tests
 * pass a fake that records every tone it was asked to play so the assertions
 * can pin the gain and duration literals above without reaching into the
 * module under test.
 */
export type SoundRuntime = {
  /** A fresh `AudioContext` (or whatever the fake stands in). May throw —
   *  `playNotificationSound` catches it and returns `false`. */
  create(): unknown;
  /** `audioCtx.state`. A fake returns `'running'` to play, anything else
   *  to refuse. */
  state(ctx: unknown): string;
  /** Resume a `suspended` context. May be a no-op for a fake. */
  resume(ctx: unknown): Promise<void>;
  /**
   * Play one tone: an oscillator of `frequency` Hz, `type` waveform, through
   * a gain of `peak` linear amplitude, starting at `startMs` (relative to
   * `ctx.currentTime`) and stopping by `stopMs`. With `toFrequency`, the pitch
   * sweeps exponentially from `frequency` to it across the tone (Fleet Live's
   * cues, #6779; #6773's transfer sounds reuse the same field). With `lowpassHz`, `attackMs`
   * and `bus`, a filter, a slower attack and a shared bus (#6773). A fake records the call. May
   * throw — `playNotificationSound` catches it and returns `false`.
   */
  playTone(ctx: unknown, opts: ToneSpec): void;
  /** Make one sound's bus (`SoundBus`); its tones pass the result as `bus`. A fake records it. */
  bus(ctx: unknown, opts: SoundBus): unknown;
};

/**
 * Read the stored choice, falling back to `'off'` for: nothing stored,
 * corrupt JSON, a value outside `SOUND_CHOICES`, or no `localStorage`
 * (private mode, throw on access). The single seam every caller goes through,
 * so a stored `chime` survives a reload and a tampered store falls back
 * rather than crashes.
 */
export function readStoredNotificationSound(): SoundChoice {
  return readLocalPref<SoundChoice>(NOTIFICATION_SOUND_KEY, SOUND_CHOICE_SCHEMA, 'off');
}

/** Write the choice. Persists across a reload; never throws. */
export function writeStoredNotificationSound(choice: SoundChoice): void {
  writeLocalPref<SoundChoice>(NOTIFICATION_SOUND_KEY, choice);
}

/** The tones of one choice: an oscillator through a gain each. Every tone ends
 *  inside 400 ms of its start and none exceeds `NOTIFICATION_SOUND_PEAK_GAIN`. Two-oscillator tones (chime,
 *  pop) schedule their partial at `+startMs` so the ear reads it as one
 *  sound, not a sequence. */
function toneSpecs(choice: NotificationSound): readonly {
  readonly frequency: number;
  readonly type: 'sine' | 'triangle' | 'square' | 'sawtooth';
  readonly peak: number;
  readonly startMs: number;
  readonly stopMs: number;
}[] {
  switch (choice) {
    case 'chime':
      return [
        {
          frequency: 880,
          type: 'sine',
          peak: NOTIFICATION_SOUND_PEAK_GAIN,
          startMs: 0,
          stopMs: 180,
        },
        {
          frequency: 1320,
          type: 'sine',
          peak: NOTIFICATION_SOUND_PEAK_GAIN,
          startMs: 30,
          stopMs: 220,
        },
      ];
    case 'ping':
      return [
        {
          frequency: 1200,
          type: 'sine',
          peak: NOTIFICATION_SOUND_PEAK_GAIN,
          startMs: 0,
          stopMs: 120,
        },
      ];
    case 'pop':
      return [
        {
          frequency: 520,
          type: 'triangle',
          peak: NOTIFICATION_SOUND_PEAK_GAIN,
          startMs: 0,
          stopMs: 90,
        },
      ];
  }
}

/**
 * Play the stored tone (or `'off'` for nothing). Reads the choice from
 * `localStorage`, plays it once if it is a tone, and never throws — the
 * bell's arrival callback and the page's Test sound button both go through
 * here. The runtime defaults to `browserSoundRuntime()` (the live APIs).
 */
export function playStoredNotificationSound(
  makeRuntime: () => SoundRuntime = browserSoundRuntime,
): Promise<boolean> {
  return playNotificationSound(readStoredNotificationSound(), makeRuntime());
}

/**
 * Play `choice` once. Answers `true` when a tone was started and `false` for:
 * `'off'`, a `create` that throws (no `AudioContext` here), a context that is
 * not running after `resume()` (the browser's autoplay policy, bounded by
 * {@link RESUME_WAIT_MS}), and any `playTone` that throws. Never rejects. A
 * context that is not running is asked to resume and AWAITED: the state flips a
 * moment after `resume()` is called, so checking it on the same line would
 * refuse a press that is about to work.
 */
export async function playNotificationSound(
  choice: SoundChoice,
  runtime: SoundRuntime,
): Promise<boolean> {
  if (choice === 'off') return false;
  let ctx: unknown;
  try {
    ctx = runtime.create();
  } catch {
    return false;
  }
  if (runtime.state(ctx) !== 'running') {
    try {
      await withDeadline(runtime.resume(ctx), RESUME_WAIT_MS, 'notification sound: resume');
    } catch {
      return false;
    }
    if (runtime.state(ctx) !== 'running') return false;
  }
  for (const spec of toneSpecs(choice)) {
    try {
      runtime.playTone(ctx, spec);
    } catch {
      return false;
    }
  }
  return true;
}

// Browser side of the seam: one `AudioContext`, reused across calls.
// `AudioContext` and `OscillatorNode` are not present in happy-dom, so the
// real impl touches them through `unknown` and casts at the boundary; a
// test passes a fake and never hits this code path.
let cachedRuntime: SoundRuntime | null = null;
export function browserSoundRuntime(): SoundRuntime {
  if (cachedRuntime !== null) return cachedRuntime;
  cachedRuntime = createBrowserSoundRuntime();
  return cachedRuntime;
}

/** A fresh browser runtime over `AudioContext`; exported for its test, which fakes the API. */
export function createBrowserSoundRuntime(): SoundRuntime {
  type AudioNodeLike = { connect(dest: unknown): void; disconnect(): void };
  type AudioCtxCtor = new () => {
    readonly state: string;
    resume(): Promise<void>;
    readonly currentTime: number;
    createOscillator(): {
      frequency: {
        setValueAtTime(value: number, start: number): void;
        exponentialRampToValueAtTime(value: number, end: number): void;
      };
      readonly type: string;
      connect(dest: unknown): void;
      start(when: number): void;
      stop(when: number): void;
    };
    createGain(): {
      gain: {
        value: number;
        setValueAtTime(value: number, start: number): void;
        linearRampToValueAtTime(value: number, end: number): void;
        exponentialRampToValueAtTime(value: number, end: number): void;
      };
      connect(dest: unknown): void;
      disconnect(): void;
    };
    createBiquadFilter(): AudioNodeLike & {
      type: string;
      frequency: { value: number };
      Q: { value: number };
    };
    createDynamicsCompressor(): AudioNodeLike;
    createDelay(): AudioNodeLike & { delayTime: { value: number } };
    readonly destination: unknown;
  };
  // Unprefixed `AudioContext` only (Safari 14.1+ ships it): a `webkit` fallback is a
  // legacy path nothing here would run or test.
  type CtorHolder = { AudioContext?: AudioCtxCtor };
  const g = globalThis as unknown as { AudioContext?: AudioCtxCtor; window?: CtorHolder };
  const Ctor: AudioCtxCtor | undefined = g.AudioContext ?? g.window?.AudioContext;
  let ctx: { readonly state: string; resume(): Promise<void> } | null = null;
  return {
    create() {
      if (Ctor === undefined) throw new Error('AudioContext not available');
      if (ctx === null) ctx = new Ctor();
      return ctx;
    },
    state(c) {
      return (c as { readonly state: string }).state;
    },
    async resume(c) {
      await (c as { resume(): Promise<void> }).resume();
    },
    playTone(c, opts) {
      const a = c as InstanceType<AudioCtxCtor>;
      const osc = a.createOscillator();
      const gain = a.createGain();
      // `exponentialRampToValueAtTime` rejects 0; floor at 0.0001 and ramp from
      // the peak down to it across the tone's window. The `setValueAtTime`
      // seeds the curve at the peak.
      const start = a.currentTime + opts.startMs / 1000;
      const stop = a.currentTime + opts.stopMs / 1000;
      osc.frequency.setValueAtTime(opts.frequency, start);
      // An exponential ramp refuses 0 and below, so a sweep floors at 20 Hz.
      if (opts.toFrequency !== undefined) {
        osc.frequency.exponentialRampToValueAtTime(Math.max(20, opts.toFrequency), stop);
      }
      // `type` is a string setter on `OscillatorNode`; the typed seam above
      // hides it from us, so assign through the `unknown` shape.
      (osc as unknown as { type: string }).type = opts.type;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(opts.peak, start + (opts.attackMs ?? 5) / 1000);
      gain.gain.exponentialRampToValueAtTime(0.0001, stop);
      if (opts.lowpassHz === undefined) osc.connect(gain);
      else {
        const lp = a.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = opts.lowpassHz;
        lp.Q.value = 6;
        osc.connect(lp);
        lp.connect(gain);
      }
      gain.connect(opts.bus ?? a.destination);
      osc.start(start);
      osc.stop(stop + 0.005);
    },
    bus(c, o) {
      const a = c as InstanceType<AudioCtxCtor>;
      const out = a.createDynamicsCompressor();
      const bus = a.createGain();
      bus.gain.value = o.gain;
      bus.connect(out);
      out.connect(a.destination);
      // The echo: a delay fed back into itself and mixed in.
      const delay = a.createDelay();
      delay.delayTime.value = o.echoMs / 1000;
      const feedback = a.createGain();
      feedback.gain.value = o.feedback;
      const wet = a.createGain();
      wet.gain.value = o.wet;
      bus.connect(delay);
      delay.connect(feedback);
      feedback.connect(delay);
      delay.connect(wet);
      wet.connect(out);
      setTimeout(() => {
        for (const n of [bus, delay, feedback, wet, out]) n.disconnect();
      }, o.releaseMs);
      return bus;
    },
  };
}
